import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Navigation } from '@/components/Navigation';
import { ScrollToTop } from '@/components/ScrollToTop';
import { TrackingProvider } from '@/components/TrackingProvider';

// Route-level code splitting: each page ships as its own chunk instead of one
// monolithic bundle. main.tsx preloads the landing route's chunk before the first
// render (preloadRoute below) so hydration never blanks the prerendered HTML.
const loaders = {
  home: () => import('@/pages/Home'),
  comoFunciona: () => import('@/pages/ComoFunciona'),
  servicios: () => import('@/pages/Servicios'),
  agencias: () => import('@/pages/Agencias'),
  socios: () => import('@/pages/Socios'),
  blog: () => import('@/pages/Blog'),
  blogPost: () => import('@/pages/BlogPost'),
  antiFraude: () => import('@/pages/AntiFraude'),
  filantropia: () => import('@/pages/Filantropia'),
  tracking: () => import('@/pages/Tracking'),
  contacto: () => import('@/pages/Contacto'),
  clientes: () => import('@/pages/Clientes'),
  crearPassword: () => import('@/pages/CrearPassword'),
  welcome: () => import('@/pages/Welcome'),
  legal: () => import('@/pages/LegalPages'),
  apps: () => import('@/pages/AppPages'),
  concursoAgencias: () => import('@/pages/ConcursoAgencias'),
};

const Home = lazy(() => loaders.home().then((m) => ({ default: m.Home })));
const ComoFunciona = lazy(() => loaders.comoFunciona().then((m) => ({ default: m.ComoFunciona })));
const Servicios = lazy(() => loaders.servicios().then((m) => ({ default: m.Servicios })));
const Agencias = lazy(() => loaders.agencias().then((m) => ({ default: m.Agencias })));
const Socios = lazy(() => loaders.socios().then((m) => ({ default: m.Socios })));
const Blog = lazy(() => loaders.blog().then((m) => ({ default: m.Blog })));
const BlogPost = lazy(() => loaders.blogPost().then((m) => ({ default: m.BlogPost })));
const AntiFraude = lazy(() => loaders.antiFraude().then((m) => ({ default: m.AntiFraude })));
const Filantropia = lazy(() => loaders.filantropia().then((m) => ({ default: m.Filantropia })));
const Tracking = lazy(() => loaders.tracking().then((m) => ({ default: m.Tracking })));
const Contacto = lazy(() => loaders.contacto().then((m) => ({ default: m.Contacto })));
const Clientes = lazy(() => loaders.clientes().then((m) => ({ default: m.Clientes })));
const CrearPassword = lazy(() => loaders.crearPassword().then((m) => ({ default: m.CrearPassword })));
const Welcome = lazy(() => loaders.welcome().then((m) => ({ default: m.Welcome })));
const Terminos = lazy(() => loaders.legal().then((m) => ({ default: m.Terminos })));
const Privacidad = lazy(() => loaders.legal().then((m) => ({ default: m.Privacidad })));
const ArticulosProhibidos = lazy(() => loaders.legal().then((m) => ({ default: m.ArticulosProhibidos })));
const Comunidad = lazy(() => loaders.legal().then((m) => ({ default: m.Comunidad })));
const Dmca = lazy(() => loaders.legal().then((m) => ({ default: m.Dmca })));
const Cookies = lazy(() => loaders.legal().then((m) => ({ default: m.Cookies })));
const PrivacidadEstatal = lazy(() => loaders.legal().then((m) => ({ default: m.PrivacidadEstatal })));
const MiDashboard = lazy(() => loaders.apps().then((m) => ({ default: m.MiDashboard })));
const MiEnvioCuba = lazy(() => loaders.apps().then((m) => ({ default: m.MiEnvioCuba })));
const MiAdmin = lazy(() => loaders.apps().then((m) => ({ default: m.MiAdmin })));
const MiCargo = lazy(() => loaders.apps().then((m) => ({ default: m.MiCargo })));
const MiRestaurante = lazy(() => loaders.apps().then((m) => ({ default: m.MiRestaurante })));
const ConcursoAgencias = lazy(() => loaders.concursoAgencias().then((m) => ({ default: m.ConcursoAgencias })));

const routeLoaders: Record<string, () => Promise<unknown>> = {
  '/': loaders.home,
  '/como-funciona': loaders.comoFunciona,
  '/servicios': loaders.servicios,
  '/plataforma': loaders.agencias,
  '/agencias': loaders.agencias,
  '/socios': loaders.socios,
  '/blog': loaders.blog,
  '/anti-fraude': loaders.antiFraude,
  '/filantropia': loaders.filantropia,
  '/rastreo': loaders.tracking,
  '/contacto': loaders.contacto,
  '/clientes': loaders.clientes,
  '/terminos': loaders.legal,
  '/privacidad': loaders.legal,
  '/articulos-prohibidos': loaders.legal,
  '/comunidad': loaders.legal,
  '/derechos-autor': loaders.legal,
  '/cookies': loaders.legal,
  '/privacidad-estatal': loaders.legal,
  '/plataforma/mi-dashboard': loaders.apps,
  '/plataforma/mi-envio-cuba': loaders.apps,
  '/plataforma/mi-admin': loaders.apps,
  '/plataforma/mi-cargo': loaders.apps,
  '/plataforma/mi-restaurante': loaders.apps,
  '/concurso-agencias-conocidas': loaders.concursoAgencias,
};

export function preloadRoute(pathname: string): Promise<unknown> {
  const clean = pathname.replace(/\/+$/, '') || '/';
  const loader =
    routeLoaders[clean] ?? (clean.startsWith('/blog/') ? loaders.blogPost : undefined);
  return loader ? loader().catch(() => undefined) : Promise.resolve();
}

function App() {
  return (
    <Router>
      <TrackingProvider>
        <ScrollToTop />
        <div className="min-h-screen bg-bg-light">
          <Navigation />
          <Suspense fallback={null}>
            <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/como-funciona" element={<ComoFunciona />} />
          <Route path="/servicios" element={<Servicios />} />
          <Route path="/plataforma" element={<Agencias />} />
          {/* One page per app in the platform (data-driven: src/apps/data.ts) */}
          <Route path="/plataforma/mi-dashboard" element={<MiDashboard />} />
          <Route path="/plataforma/mi-envio-cuba" element={<MiEnvioCuba />} />
          <Route path="/plataforma/mi-admin" element={<MiAdmin />} />
          <Route path="/plataforma/mi-cargo" element={<MiCargo />} />
          <Route path="/plataforma/mi-restaurante" element={<MiRestaurante />} />
          {/* Legacy URL — the tab was renamed Plataforma; _redirects also 301s this server-side */}
          <Route path="/agencias" element={<Navigate to="/plataforma" replace />} />
          <Route path="/socios" element={<Socios />} />
          {/* TEMPORARY promo page — remove with the Home banner when the promo ends.
              /sorteo was renamed 2026-08-23; _redirects 301s the old URL. */}
          <Route path="/concurso-agencias-conocidas" element={<ConcursoAgencias />} />
          <Route path="/sorteo" element={<Navigate to="/concurso-agencias-conocidas" replace />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/anti-fraude" element={<AntiFraude />} />
          <Route path="/filantropia" element={<Filantropia />} />
          <Route path="/rastreo" element={<Tracking />} />
          <Route path="/terminos" element={<Terminos />} />
          <Route path="/privacidad" element={<Privacidad />} />
          <Route path="/articulos-prohibidos" element={<ArticulosProhibidos />} />
          <Route path="/comunidad" element={<Comunidad />} />
          <Route path="/derechos-autor" element={<Dmca />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/privacidad-estatal" element={<PrivacidadEstatal />} />
          {/* /envios never had real content — its closest successor is the prohibited-items policy */}
          <Route path="/envios" element={<Navigate to="/articulos-prohibidos" replace />} />
          <Route path="/contacto" element={<Contacto />} />
          <Route path="/clientes" element={<Clientes />} />
          {/* Customer-facing Firebase email-action handler. Customers must
              never be sent to app.mienviocuba.com — that is staff territory. */}
          <Route path="/crear-password" element={<CrearPassword />} />
          {/* Where a customer lands once their password is set. */}
          <Route path="/welcome" element={<Welcome />} />
            </Routes>
          </Suspense>
        </div>
      </TrackingProvider>
    </Router>
  );
}

export default App;
